import moment from 'moment';
import React, { Component } from 'react';
import { Query, withApollo } from 'react-apollo';
import { Button, Input, message, Modal, Radio } from 'antd';
import BanHistory from './BanHistory';
import BanUserForm from './BanUserForm';
import Loading from '../../components/Loading';
import {
  GET_BANNED_USERS,
  GET_BANNED_ANOM_USERS,
  BAN_USER,
  UN_BAN_USER,
  CLEAR_EXPIRED_BANS,
} from '../../api/banned';
import './index.css';

const statusLabels = { active: '生效中', expired: '已到期', invalid: '日期异常' };

const banStatus = endDate => {
  if (typeof endDate !== 'string' || !endDate.trim()) return 'invalid';
  const expiry = moment(endDate, 'YYYY-MM-DD', true);
  if (!expiry.isValid()) return 'invalid';
  return endDate === '2099-12-31' || moment().isBefore(expiry) ? 'active' : 'expired';
};

const UsersList = ({ query, name, filter, onEdit, onRemove, onHistory }) => (
  <div>
    <header className="ban-list-heading">
      <h2>{name === 'bannedUsers' ? '禁言' : '限制匿名'}</h2>
      <p>{name === 'bannedUsers' ? '禁止发帖、评论和发送私信' : '禁止匿名发言和发送私信'}</p>
    </header>
    <Query query={query} fetchPolicy="network-only">
      {({ loading, error, data }) => {
        if (loading) return <Loading />;
        if (error) return <p>加载处罚列表失败</p>;
        const users = ((data && data[name]) || []).map(user => ({
          ...user, status: banStatus(user.endDate),
        }));
        const visibleUsers = users.filter(user => filter === 'all' || user.status === filter);
        if (!visibleUsers.length) return <div className="ban-empty">暂无符合筛选条件的记录</div>;
        return visibleUsers.map(user => (
          <div className="banned-item" key={`${name}-${user._id}`}>
            <div className="banned-user">
              <img
                className="ban-user-avatar"
                src={user.avatarUrl}
                alt=""
              />
              <div>
                <div className="ban-user-name">{user.nickname || '未设置昵称'}</div>
                <code className="banned-user-id" title="用户 ID">{user._id}</code>
              </div>
            </div>
            <div className="ban-card-status">
              <span>截止日期</span>
              <strong>{user.endDate === '2099-12-31' ? '永久' : user.endDate || '—'}</strong>
              <span className={`ban-status-pill ban-status-${user.status}`}>{statusLabels[user.status]}</span>
            </div>
            <div className="banned-btn-group">
              <Button size="small" onClick={() => onEdit(user)}>调整期限</Button>
              {user.status === 'active' && <Button size="small" onClick={() => onRemove(user)}>解封</Button>}
              <Button size="small" icon="history" onClick={() => onHistory(user._id)}>处罚历史</Button>
            </div>
          </div>
        ));
      }}
    </Query>
  </div>
);

const BannedUsersList = ({ ...args }) => (
  <UsersList query={GET_BANNED_USERS} name="bannedUsers" {...args} />
);
const BannedAnomUsersList = ({ ...args }) => (
  <UsersList query={GET_BANNED_ANOM_USERS} name="bannedAnomUsers" {...args} />
);

class BannedList extends Component {
  state = {
    filter: 'active',
    modalVisible: false,
    currentBannedUser: null,
    releaseUser: null,
    releaseReason: '',
    historyUserId: null,
    submitting: false,
  };

  closeModal = () => {
    this.setState({
      modalVisible: false,
      currentBannedUser: null,
    });
  };

  addBannedUser = () => {
    this.setState({
      modalVisible: true,
      currentBannedUser: null,
    });
  };

  handleRemoveBan = user => this.setState({ releaseUser: user, releaseReason: '' });

  onConfirmRelease = async () => {
    const { releaseUser, releaseReason, submitting } = this.state;
    if (!releaseUser || submitting) return;
    if (!releaseReason.trim()) {
      message.error('请输入解封原因');
      return;
    }
    this.setState({ submitting: true });
    try {
      await this.updateBan(UN_BAN_USER, {
        _id: releaseUser._id, isAnom: releaseUser.isAnom, reason: releaseReason.trim(),
      });
      this.setState({ releaseUser: null, releaseReason: '' });
      message.success('已解除处罚');
    } catch (error) {
      message.error(error.message || '解封失败');
    } finally {
      this.setState({ submitting: false });
    }
  };

  updateBan = (mutation, variables) => this.props.client.mutate({
    mutation,
    variables,
    refetchQueries: [{ query: GET_BANNED_USERS }, { query: GET_BANNED_ANOM_USERS }],
    awaitRefetchQueries: true,
  });

  clearExpired = () => Modal.confirm({
    title: '清理已到期的处罚记录？',
    content: '将清理禁言和匿名限制中已到期的条目，清理记录仍可在处罚历史中查询。',
    okText: '确认清理',
    okType: 'danger',
    cancelText: '取消',
    onOk: async () => {
      try {
        const { data } = await this.updateBan(CLEAR_EXPIRED_BANS);
        message.success(`已清理 ${data.clearExpiredBans} 条到期记录`);
      } catch (error) {
        message.error(error.message || '清理失败');
        throw error;
      }
    },
  });

  openHistory = userId => this.setState({ historyUserId: userId });

  handleEditBan = user => {
    this.setState({
      modalVisible: true,
      currentBannedUser: user,
    });
  };

  setRef = ref => {
    this.formRef = ref;
  };

  onConfirmBan = () => {
    const { form } = this.formRef.props;
    form.validateFields(async (err, values) => {
      if (err || this.state.submitting) return;
      this.setState({ submitting: true });
      try {
        await this.updateBan(BAN_USER, { ...values, reason: values.reason.trim() });
        form.resetFields();
        this.closeModal();
        message.success('处罚已保存');
      } catch (error) {
        message.error(error.message || '保存失败');
      } finally {
        this.setState({ submitting: false });
      }
    });
  };

  renderBanModal = () => {
    const { currentBannedUser, modalVisible } = this.state;
    return (
      <Modal
        visible={modalVisible}
        title={currentBannedUser ? '调整处罚期限' : '新增处罚'}
        destroyOnClose
        okText="保存处罚"
        cancelText="取消"
        confirmLoading={this.state.submitting}
        onCancel={() => !this.state.submitting && this.closeModal()}
        onOk={this.onConfirmBan}
      >
        <BanUserForm
          onSubmit={this.onConfirmBan}
          bannedUser={currentBannedUser}
          onSetRef={this.setRef}
        />
      </Modal>
    );
  };

  render() {
    const { releaseUser, releaseReason, historyUserId, submitting, filter } = this.state;
    return (
      <section className="ban-page">
        <header className="ban-page-header">
          <h1>处罚管理</h1>
          <div className="ban-page-actions">
            <Button icon="history" onClick={() => this.openHistory('')}>查询处罚历史</Button>
            <Button type="primary" icon="plus" onClick={this.addBannedUser}>新增处罚</Button>
          </div>
        </header>
        <div className="ban-filter-bar">
          <Radio.Group value={filter}
            onChange={event => this.setState({ filter: event.target.value })}>
            <Radio.Button value="active">生效中</Radio.Button>
            <Radio.Button value="expired">已到期</Radio.Button>
            <Radio.Button value="invalid">日期异常</Radio.Button>
            <Radio.Button value="all">全部</Radio.Button>
          </Radio.Group>
          {filter === 'expired' && (
            <Button type="danger" icon="delete" onClick={this.clearExpired}>清理已到期</Button>
          )}
        </div>
        <div className="banned-list">
          <BannedUsersList
            filter={filter}
            onEdit={this.handleEditBan}
            onRemove={this.handleRemoveBan}
            onHistory={this.openHistory}
          />
          <BannedAnomUsersList
            filter={filter}
            onEdit={this.handleEditBan}
            onRemove={this.handleRemoveBan}
            onHistory={this.openHistory}
          />
        </div>
        {this.renderBanModal()}
        <Modal title="解除处罚" visible={!!releaseUser} confirmLoading={submitting}
          okText="确认解除" cancelText="取消"
          onOk={this.onConfirmRelease}
          onCancel={() => !submitting && this.setState({ releaseUser: null })}>
          {releaseUser && <p>{releaseUser.nickname || releaseUser._id} · {releaseUser.isAnom ? '限制匿名（禁止发私信）' : '禁言'}</p>}
          <Input.TextArea value={releaseReason} maxLength={500} placeholder="请输入解封原因"
            onChange={event => this.setState({ releaseReason: event.target.value })} />
        </Modal>
        <Modal title="处罚历史" visible={historyUserId !== null} width="min(1060px, calc(100vw - 32px))" footer={null}
          destroyOnClose onCancel={() => this.setState({ historyUserId: null })}>
          {historyUserId !== null && <BanHistory key={historyUserId} initialUserId={historyUserId} />}
        </Modal>
      </section>
    );
  }
}

export default withApollo(BannedList);
